import html2canvas from "html2canvas";
import { logger } from "~/common/logger";

export interface DownloadChartOptions {
  element: HTMLDivElement;
  document: Document;
  filename?: string;
}

/**
 * Downloads a chart element as a PNG image using html2canvas
 */
export const downloadChart = async ({
  element,
  document,
  filename = "chart-download",
}: DownloadChartOptions): Promise<void> => {
  if (!element || !document) return;

  try {
    const canvas = await html2canvas(element, {
      backgroundColor: "#ffffff",
      scale: 2,
      useCORS: true,
      allowTaint: true,
      height: element.scrollHeight + 36,
      width: element.scrollWidth + 48,
      removeContainer: true,
      x: -24,
      y: -16,
      logging: false,
      windowWidth: 1280,
      windowHeight: 3000,
      onclone: (clonedDoc) => {
        const style = clonedDoc.createElement("style");
        style.textContent = `
          @font-face {
            font-family: "Proxima Nova";
            src: url("https://cdn.morningconsultintelligence.com/MCI-1.1/fonts/PN-Regular.woff2") format("woff2");
            font-weight: 400;
            font-style: normal;
          }
          `;
        clonedDoc.head.appendChild(style);

        clonedDoc.querySelectorAll("*").forEach((el) => {
          (el as HTMLElement).style.setProperty(
            "font-family",
            '"Proxima Nova", sans-serif',
            "important"
          );
        });
      },
    });

    // Create download link
    const link = document.createElement("a");
    link.download = `${filename}.png`;
    link.href = canvas.toDataURL("image/png");

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    logger.error(error, "Error downloading chart");
  }
};

/**
 * Generates a filename from a headline string by replacing non-alphanumeric characters
 */
export const generateChartFilename = (headline?: string): string => {
  if (!headline) return "chart-download";

  return `chart-${headline.replace(/[^a-z0-9]/gi, "_").toLowerCase()}`;
};
